import React, { useState, useEffect } from 'react'
import UserManagementHeader from './UserManagementHeader'
import UserTable from './UserTable'
import { Plus, Search } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { CommonButton } from '@/components/ui/common-button'
import AddUserDialog from './addusers/AddUserDialog'
import userService from '@/services/userService'
import Loader, { FullScreenLoader, TableBlurLoader } from '../ui/loader'
import { usePagination } from '@/hooks/usePagination'
import { useAuth } from '@/contexts/AuthContext'
import { CommonSearchInput } from '@/components/ui/common-search-input'
import { useSearchParams } from 'react-router-dom'

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [error, setError] = useState(null)
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false)
  const [totalUsers, setTotalUsers] = useState(0)

  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 10 })
  const { userProfile } = useAuth();

  const { toast } = useToast()

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  const canManageUsers = () => {
    const userRole = userProfile?.role?.toLowerCase();
    return userRole === 'ipl_super_admin' || userRole === 'transporter' || userRole === 'port_admin' || userRole === 'field_officer';
  };

  const canAddUsers = () => {
    const userRole = userProfile?.role?.toLowerCase();
    return userRole === 'ipl_super_admin' || userRole === 'transporter' || userRole === 'port_admin' || userRole === 'field_officer';
  };

  const isTransporter = userProfile?.role === 'transporter';

  useEffect(() => {
    const search = searchParams.get('search') || '';
    setSearchQuery(search);

    if (!canManageUsers()) {
      setLoading(false);
      return;
    }

    fetchUsers(search)
  }, [currentPage, pageSize, searchParams.get('search')])


  useEffect(() => {
    const timer = setTimeout(() => {
      const currentSearchParam = searchParams.get('search') || '';
      if (searchQuery !== currentSearchParam) {
        handleSearch(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchUsers = async (query = searchQuery) => {
    try {
      setLoading(true)
      const skip = (currentPage - 1) * pageSize
      const userData = await userService.getUsers(skip, pageSize, query)

      let usersData, totalCount;

      if (userData && typeof userData === 'object' && Array.isArray(userData.data)) {
        usersData = userData.data
        totalCount = userData.total || userData.count || usersData.length
      } else if (Array.isArray(userData)) {
        usersData = userData
        totalCount = userData.length < pageSize ? skip + userData.length : skip + userData.length + 1
      } else if (userData && typeof userData === 'object' && Array.isArray(userData.users)) {
        usersData = userData.users
        totalCount = userData.total || userData.count || usersData.length
      } else {
        usersData = []
        totalCount = 0
      }

      const transformedUsers = usersData.map(user => {
        const baseUser = {
          id: user.id,
          name: user.username,
          phone_number: user.phone_number || '',
          role: formatRole(user.role),
          status: user.is_active ? 'Active' : 'Inactive',
          lastLogin: user.created_at ? new Date(user.created_at).toLocaleString() : 'Never'
        };

        if (!isTransporter) {
          baseUser.email = user.email;
        }

        return baseUser;
      })

      setUsers(transformedUsers)
      setTotalUsers(totalCount)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError('Failed to load users')
      toast({
        title: "Error",
        description: "Failed to load users. Please try again.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }

  const formatRole = (role) => {
    if (!role) return 'User'
    const cleanRole = role.toLowerCase();
    if (cleanRole === 'field_officer') return 'Field Officer';
    if (cleanRole === 'account_head') return 'Account Head';
    if (cleanRole === 'region_head') return 'Region Head';
    if (cleanRole === 'ipl_admin') return 'IPL Admin';
    return role
      .replace('ipl_', '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const handleAddUser = async (newUserData) => {
    try {
      const newUser = await userService.createUser(newUserData)
      const transformedUser = {
        id: newUser.id,
        name: newUser.username,
        email: newUser.email,
        phone_number: newUser.phone_number || '',
        role: formatRole(newUser.role),
        status: newUser.is_active ? 'Active' : 'Inactive',
        lastLogin: newUser.created_at ? new Date(newUser.created_at).toLocaleString() : 'Never'
      }

      fetchUsers()

      setIsAddUserDialogOpen(false)
      toast({
        title: "User Added",
        description: `${newUser.username} has been successfully added to the system.`,
      })
    } catch (err) {
      console.error('Failed to add user:', err)
      if (err.message) {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        })
      } else {
        toast({
          title: "Error",
          description: "Failed to add user. Please try again.",
          variant: "destructive",
        })
      }
    }
  }

  const handleDeleteUser = async (userId) => {
    try {
      await userService.deleteUser(userId)
      return;
    } catch (err) {
      console.error('Failed to delete user:', err)
      throw err;
    }
  }

  const handleSearch = (query) => {
    setSearchQuery(query);
    const newParams = new URLSearchParams(searchParams);

    if (query) {
      newParams.set('search', query);
    } else {
      newParams.delete('search');
    }

    newParams.set('page', '1');
    setPage(1);

    setSearchParams(newParams);
  }

  const handleSearchInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);
  }

  const handleClearSearch = () => {
    handleSearch('');
  }

  if (!canManageUsers()) {
    return (
      <div className="">

      </div>
    );
  }

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>
  }

  const totalPages = Math.ceil(totalUsers / pageSize)

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            {isTransporter ? 'Driver Management' : 'User Management'}
          </h1>
          <p className="text-xs text-muted-foreground">
            {isTransporter ? 'Manage and monitor registered fleet drivers.' : 'Manage platform users, roles, and security access parameters.'}
          </p>
        </div>
        {canAddUsers() && (
          <CommonButton
            onClick={() => setIsAddUserDialogOpen(true)}
            variant="default"
            icon={Plus}
          >
            {isTransporter ? 'Add Driver' : 'Add User'}
          </CommonButton>
        )}
      </div>

      <UserManagementHeader users={users} />

      <div className="mb-4 w-full mt-4">
        <div className="md:w-1/3">
          <CommonSearchInput
            placeholder="Search by phone number..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onSearch={handleSearch}
            maxLength={50}
          />
        </div>
      </div>

      <TableBlurLoader
        isLoading={loading}
        className="mt-4"
      >
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform" style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {users.length > 0 ? (
              <UserTable
                users={users}
                setUsers={setUsers}
                currentPage={currentPage}
                totalPages={totalPages}
                totalUsers={totalUsers}
                pageSize={pageSize}
                onPageChange={setPage}
                onDeleteUser={handleDeleteUser}
                isTransporter={isTransporter}
              />
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                {loading ? "" : "No data available"}
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>

      <AddUserDialog
        open={isAddUserDialogOpen}
        onOpenChange={setIsAddUserDialogOpen}
        onAddUser={handleAddUser}
      />
    </div>
  )
}

export default UserManagement